import { useState, useCallback, useEffect, useMemo } from 'react';
import useAuthStore from '@entities/auth/model/useAuthStore';

import Alert from '@shared/components/atoms/Alert';
import Button from '@shared/components/atoms/Button';
import Confirm from '@shared/components/atoms/Confirm';
import MainLayout from '@shared/components/organisms/MainLayout/MainLayout';
import { useAlert, useConfirm, useIsMobile } from '@shared/hooks';
import ContentCard from '@shared/components/organisms/ContentCard/ContentCard';
import {
  useSignLineListQuery,
  useSignLineInfoQuery,
  useSignLineRemoveMutation,
} from '@features/signLine/hooks';
import { useEmployeeInfosQuery } from '@features/employee/hooks';
import ApprovalLineSettingModal from '@pages/settings/components/ApprovalLineSettingModal/ApprovalLineSettingModal';
import { mapSignLineMembers } from '@features/signLine/utils/mapSignLineMembers';
import MobileApprovalLinePage from './components/mobile/MobileApprovalLinePage';

import styles from './ApprovalLineSettingPage.module.scss';

function ApprovalLineSettingPage() {
  // 로그인한 사용자의 사번 가져오기
  const myEnumber = useAuthStore((state) => state.enumber);
  const isMobile = useIsMobile();
  const [approvalLines, setApprovalLines] = useState([]);
  const [selectedLineId, setSelectedLineId] = useState(null);

  // 회원 상세 데이터
  const [members, setMembers] = useState([]);

  // 1) 라인 목록 조회
  const { data: signLineListData, refetch: refetchList } = useSignLineListQuery();

  // 2) 해당 라인의 상세 조회
  const { data: signLineInfoData, refetch: refetchInfo } = useSignLineInfoQuery(selectedLineId, {
    enabled: !!selectedLineId && !isMobile, // 모바일일 때는 조회하지 않음
  });

  // 라인 목록 가공
  useEffect(() => {
    if (!signLineListData) return;

    const list = signLineListData?.resultData?.signLineList || [];
    setApprovalLines(
      list.map((item) => ({
        id: item.idx,
        name: item.title,
      })),
    );
  }, [signLineListData]);

  // 라인 상세(member 번호 리스트) 변경
  useEffect(() => {
    if (!selectedLineId) {
      setMembers([]);
      return;
    }

    if (signLineInfoData?.resultData) {
      setMembers(mapSignLineMembers(signLineInfoData.resultData));
    }
  }, [signLineInfoData, selectedLineId]);

  // 3) members(enumber만 있음) → 사원 상세정보 병렬 조회
  const employeeQueries = useEmployeeInfosQuery(members);

  // 4) 병합된 enrichedMember 계산
  const enrichedMembers = useMemo(() => {
    if (!members.length) return [];

    return members
      .map((member, idx) => {
        const data = employeeQueries[idx]?.data?.resultData;

        return {
          ...member,
          name: data?.name ?? '',
          team: data?.team ?? '',
          position: data?.position ?? '',
        };
      })
      .filter((member) => member.enumber !== myEnumber); // 로그인한 사용자 제외
  }, [members, employeeQueries]);

  // 모달 & Alert 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLine, setEditingLine] = useState(null);

  const { alertState, showAlert, closeAlert } = useAlert();
  const { confirmState, showConfirm, closeConfirm } = useConfirm();

  // 선택된 결재라인 memoization
  const selectedLine = useMemo(() => {
    return approvalLines.find((l) => l.id === selectedLineId) || null;
  }, [approvalLines, selectedLineId]);

  // 라인 선택
  const handleSelectLine = useCallback(
    (lineId) => {
      // 같은 항목 다시 선택하면 초기화하지 않음
      if (selectedLineId === lineId) return;

      setSelectedLineId(lineId);
      setMembers([]);
    },
    [selectedLineId],
  );
  // 삭제 리액트 쿼리
  const signLineRemoveMutation = useSignLineRemoveMutation({
    onSuccess: () => {
      refetchList();
      setSelectedLineId(null);
      showAlert('결재라인이 삭제되었습니다.');
    },
    onError: () => {
      showAlert('삭제에 실패했습니다.');
    },
  });

  // 삭제
  const handleDelete = useCallback(() => {
    if (!selectedLineId) return showAlert('삭제할 결재라인을 선택해주세요.');

    showConfirm('결재라인을 삭제하시겠습니까?', () => {
      // 1) 서버 삭제 요청
      signLineRemoveMutation.mutate(
        { idx: selectedLineId },
        {
          onSettled: () => {
            closeConfirm();
          },
        },
      );
    });
  }, [selectedLineId, showAlert, showConfirm, closeConfirm, signLineRemoveMutation]);

  // 수정
  const handleEdit = useCallback(() => {
    if (!selectedLineId) return showAlert('수정할 결재라인을 선택해주세요.');

    closeAlert();

    const line = approvalLines.find((l) => l.id === selectedLineId);
    setEditingLine({
      ...line,
      members: enrichedMembers,
    });
    setIsModalOpen(true);
  }, [selectedLineId, approvalLines, showAlert, enrichedMembers, closeAlert]);

  // 추가
  const handleAdd = useCallback(() => {
    closeAlert();

    setEditingLine(null);
    setIsModalOpen(true);
  }, [closeAlert]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingLine(null);
  }, []);

  // 저장 완료 (모달에서 성공 시 호출; 성공 Alert는 "부모"에서만 노출)
  const handleSaveComplete = useCallback(
    (savedLine = {}) => {
      if (editingLine) {
        // 수정
        setApprovalLines((prev) =>
          prev.map((line) =>
            line.id === editingLine.id
              ? { ...line, ...savedLine, name: savedLine.name ?? line.name }
              : line,
          ),
        );
        showAlert('결재라인이 수정되었습니다.');
      } else {
        // 추가
        setApprovalLines((prev) => [...prev, { id: Date.now(), ...savedLine }]);
        showAlert('결재라인이 등록되었습니다.');
      }

      refetchList();
      if (selectedLineId) refetchInfo();

      setIsModalOpen(false);
      setEditingLine(null);
    },
    [editingLine, showAlert, refetchList, refetchInfo, selectedLineId],
  );

  // 모바일일 때는 모바일 페이지 렌더링
  if (isMobile) {
    return <MobileApprovalLinePage />;
  }

  return (
    <MainLayout>
      <ContentCard className={styles.contentCardMarginFix}>
        <div className={styles.approvalLineSettingPage}>
          <div className={styles.sectionTitle}>자주 쓰는 결재라인 목록</div>

          <div className={styles.mainContent}>
            {/* 왼쪽 목록 */}
            <div className={styles.lineListSection}>
              <div className={styles.tableContainer}>
                <table className={styles.lineListTable}>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>결재라인명</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvalLines.length ? (
                      approvalLines.map((line, idx) => (
                        <tr
                          key={line.id}
                          className={selectedLineId === line.id ? styles.selectedRow : ''}
                          onClick={() => handleSelectLine(line.id)}
                        >
                          <td>{idx + 1}</td>
                          <td>{line.name}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="2" className={styles.emptyCell}>
                          등록된 결재라인이 없습니다.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className={styles.buttonGroup}>
                <Button onClick={handleDelete}>삭제</Button>
                <Button onClick={handleEdit}>수정</Button>
                <Button onClick={handleAdd}>추가</Button>
              </div>
            </div>

            {/* 오른쪽 구성원 목록 */}
            <div className={styles.memberListSection}>
              <div className={styles.tableContainer}>
                <table className={styles.memberListTable}>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>결재종류</th>
                      <th>부서</th>
                      <th>이름</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedLine && enrichedMembers.length ? (
                      enrichedMembers.map((m, idx) => (
                        <tr key={m.enumber || idx}>
                          <td>{idx + 1}</td>
                          <td>{m.role}</td>
                          <td>{m.team || '-'}</td>
                          <td>{m.name}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className={styles.emptyCell}>
                          {selectedLine
                            ? '구성원이 없습니다.'
                            : '결재라인을 선택하면 구성원이 표시됩니다.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </ContentCard>

      <ApprovalLineSettingModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveComplete}
        editingLine={editingLine}
      />

      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
      <Confirm
        isOpen={confirmState.isOpen}
        message={confirmState.message}
        onConfirm={confirmState.onConfirm}
        onCancel={closeConfirm}
      />
    </MainLayout>
  );
}

export default ApprovalLineSettingPage;
